import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { jobsEndpoint } from "../constants";

const EmployeeManager = () => {
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState("");
  const [workers, setWorkers] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [workersLoading, setWorkersLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [editingWorker, setEditingWorker] = useState(null);
  const [editingIncome, setEditingIncome] = useState("");
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // Fetch all jobs with workers on component mount
  useEffect(() => {
    const fetchJobsWithWorkers = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${jobsEndpoint}my-jobs-workers`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
          }
        });
        setJobs(response.data.jobs || []);
      } catch (err) {
        setError("Failed to load jobs and workers.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchJobsWithWorkers();
  }, [token]);

  // Fetch workers for selected job
  const fetchWorkersForJob = async (jobId) => {
    try {
      setWorkersLoading(true);
      const response = await axios.get(`${jobsEndpoint}job/${jobId}/workers`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        }
      });
      setWorkers(response.data.workers || []);
      setSelectedJob(response.data.job);
    } catch (err) {
      setError("Failed to load workers for selected job.");
      console.error(err);
    } finally {
      setWorkersLoading(false);
    }
  };

  const handleJobSelect = (jobId) => {
    setSelectedJobId(jobId);
    if (jobId) {
      fetchWorkersForJob(jobId);
    } else {
      setWorkers([]);
      setSelectedJob(null);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const toggleDropdown = (workerId) => {
    setDropdownOpen(dropdownOpen === workerId ? null : workerId);
  };

  const handleEditIncome = (worker) => {
    setEditingWorker(worker.id);
    setEditingIncome(worker.income || "");
    setDropdownOpen(null);
  };

  const handleSaveIncome = async (workerId) => {
    try {
      await axios.patch(`${jobsEndpoint}worker/${workerId}/update`, {
        income: parseFloat(editingIncome)
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        }
      });
      
      // Refresh workers for current job
      if (selectedJobId) {
        await fetchWorkersForJob(selectedJobId);
      }
      setEditingWorker(null);
      setEditingIncome("");
    } catch (err) {
      console.error("Failed to update worker income:", err);
      setError("Failed to update worker income.");
    }
  };

  const handleCancelEdit = () => {
    setEditingWorker(null);
    setEditingIncome("");
  };

  const handleRemoveWorker = async (workerId, workerName) => {
    if (window.confirm(`Are you sure you want to remove ${workerName} from this job?`)) {
      try {
        await axios.delete(`${jobsEndpoint}worker/${workerId}/remove`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
          }
        });
        
        // Refresh workers for current job
        if (selectedJobId) {
          await fetchWorkersForJob(selectedJobId);
        }
        setDropdownOpen(null);
      } catch (err) {
        console.error("Failed to remove worker:", err);
        setError("Failed to remove worker.");
      }
    }
  };

  const filteredWorkers = useMemo(() => {
    if (!searchTerm) return workers;
    
    const searchLower = searchTerm.toLowerCase();
    return workers.filter(worker =>
      worker.user?.username?.toLowerCase().includes(searchLower) ||
      worker.user?.first_name?.toLowerCase().includes(searchLower) ||
      worker.user?.last_name?.toLowerCase().includes(searchLower)
    );
  }, [workers, searchTerm]);

  const formatCurrency = (amount) => {
    if (!amount) return "Not set";
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setDropdownOpen(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className="lg:p-8">
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Job Workers Management</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage workers assigned to your job postings.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/jobs")}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            My Jobs
          </button>
        </div>
      </div>

      {/* Loading/Error */}
      {loading && <p className="text-gray-500">Loading jobs...</p>}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Job Selection */}
      {!loading && !error && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select a job to manage workers
          </label>
          <select
            value={selectedJobId}
            onChange={(e) => handleJobSelect(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent min-w-64"
          >
            <option value="">Choose a job...</option>
            {jobs.map((job) => (
              <option key={job.id} value={job.id}>
                {job.title} ({job.workers_count} workers)
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Workers Table */}
      {selectedJobId && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="pt-3 px-5">
            <h3 className="text-sm font-semibold text-gray-900">
              Workers for: {selectedJob?.title}
            </h3>
          </div>
          <div className="flex gap-3 items-end py-4 px-5 border-b border-gray-200">
            <h3 className="text-2xl text-gray-900 font-semibold">
              {workersLoading ? "Loading..." : filteredWorkers.length}
            </h3>
            <div className="flex items-center gap-1 text-sm text-blue-600">
              <span>👥</span>
              <span className="text-gray-600 ml-1">Active workers</span>
            </div>
          </div>

          {/* Search */}
          {!workersLoading && workers.length > 0 && (
            <div className="px-5 py-4 border-b border-gray-200">
              <input
                type="text"
                placeholder="Search workers..."
                value={searchTerm}
                onChange={handleSearch}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent"
              />
            </div>
          )}

          <div>
            {/* Table Header */}
            <div className="px-5 py-3 grid md:grid-cols-6 grid-cols-4 border-b border-gray-200">
              <div className="flex gap-3 items-center md:col-span-2 col-span-2">
                <input type="checkbox" className="rounded border-gray-300" />
                <h5 className="text-gray-500 font-semibold">Worker</h5>
              </div>
              <div className="grid md:grid-cols-4 grid-cols-2 md:col-span-4 col-span-2">
                <h5 className="text-gray-500 font-semibold text-xs">Income</h5>
                <h5 className="text-gray-500 font-semibold text-xs hidden md:block">Date Joined</h5>
                <h5 className="text-gray-500 font-semibold text-xs hidden md:block">Last Updated</h5>
                <h5 className="text-gray-500 font-semibold text-xs">Actions</h5>
              </div>
            </div>

            {/* Loading State */}
            {workersLoading && (
              <div className="p-8 text-center">
                <p className="text-gray-500">Loading workers...</p>
              </div>
            )}

            {/* Empty State */}
            {!workersLoading && filteredWorkers.length === 0 && (
              <div className="p-8 text-center">
                <div className="text-gray-400 text-6xl mb-4">👥</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {workers.length === 0 ? "No workers assigned" : "No workers found"}
                </h3>
                <p className="text-gray-600">
                  {workers.length === 0 
                    ? "Workers will appear here when they are assigned to this job."
                    : "Try adjusting your search criteria."
                  }
                </p>
              </div>
            )}

            {/* Workers List */}
            {!workersLoading && filteredWorkers.map((worker) => (
              <div key={worker.id} className="grid md:grid-cols-6 grid-cols-4 items-center p-4 border-b border-gray-200">
                <div className="flex items-center gap-3 md:col-span-2 col-span-2">
                  <input type="checkbox" className="rounded border-gray-300" />
                  <div>
                    <p className="md:font-medium font-bold text-sm text-gray-900">
                      {worker.user?.first_name && worker.user?.last_name 
                        ? `${worker.user.first_name} ${worker.user.last_name}`
                        : worker.user?.username || 'Unknown User'
                      }
                    </p>
                    <p className="text-xs text-gray-500">
                      @{worker.user?.username || 'unknown'}
                    </p>
                  </div>
                </div>

                <div>
                  {editingWorker === worker.id ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={editingIncome}
                        onChange={(e) => setEditingIncome(e.target.value)}
                        className="w-20 px-2 py-1 text-xs border border-gray-300 rounded"
                        placeholder="Amount"
                      />
                      <button
                        onClick={() => handleSaveIncome(worker.id)}
                        className="text-green-600 hover:text-green-800 text-xs"
                      >
                        ✓
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="text-red-600 hover:text-red-800 text-xs"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div>
                      <p className="font-semibold text-sm text-gray-900">
                        {formatCurrency(worker.income)}
                      </p>
                      {worker.income && (
                        <p className="text-xs text-gray-500">per year</p>
                      )}
                    </div>
                  )}
                </div>

                <div className="hidden md:block">
                  <p className="font-medium text-xs text-gray-500">
                    {formatDate(worker.date_published)}
                  </p>
                </div>

                <div className="hidden md:block">
                  <p className="font-medium text-xs text-gray-500">
                    {formatDate(worker.date_updated)}
                  </p>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-xs bg-green-100 text-green-600 border border-green-400 py-1 px-2 rounded-md">
                    Active
                  </span>
                  <div className="relative">
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDropdown(worker.id);
                      }}
                      className="text-gray-600 cursor-pointer text-xl font-bold ml-2"
                    >
                      &#8942;
                    </span>
                    {dropdownOpen === worker.id && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                        <div className="py-1">
                          <button
                            onClick={() => handleEditIncome(worker)}
                            className="block w-full text-left px-4 py-2 text-sm text-blue-700 hover:bg-blue-50"
                          >
                            Edit Income
                          </button>
                          <button
                            onClick={() => handleRemoveWorker(
                              worker.id, 
                              worker.user?.first_name && worker.user?.last_name 
                                ? `${worker.user.first_name} ${worker.user.last_name}`
                                : worker.user?.username || 'Unknown User'
                            )}
                            className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                          >
                            Remove Worker
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          {!workersLoading && filteredWorkers.length > 0 && (
            <div className="p-5 bg-blue-50 border-t border-blue-200">
              <div className="flex items-center gap-2 text-blue-800">
                <span>ℹ️</span>
                <p className="text-sm">
                  Managing {filteredWorkers.length} worker{filteredWorkers.length !== 1 ? 's' : ''} 
                  {selectedJob ? ` for "${selectedJob.title}"` : ''}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmployeeManager;